import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  IconButton,
  LinearProgress,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronDown, Lock, RotateCcw, Save, Search, ShieldCheck, Users, X } from 'lucide-react';
import { useRoles } from '../../hooks/useRoles';
import type { RoleClaimResponse, RoleResponse } from '../../types/role';
import {
  buildFallbackGroups,
  buildRegistryGroups,
  filterGroups,
  groupClaims,
  type PermissionGroup,
  type PermissionSection,
} from './permissionGroups';

// Admin roles always hold every permission (the backend enforces it), so they stay view-only.
const FULL_ACCESS_ROLES = ['SystemAdmin', 'InstituteAdmin'];

interface PermissionEditorProps {
  role: RoleResponse;
  onDirtyChange: (dirty: boolean) => void;
}

const stateOf = (claims: RoleClaimResponse[]) => {
  const selected = claims.filter((c) => c.selected).length;
  return { selected, all: claims.length > 0 && selected === claims.length, some: selected > 0 && selected < claims.length };
};

export function PermissionEditor({ role, onDirtyChange }: PermissionEditorProps) {
  const readOnly = role.isGlobal && FULL_ACCESS_ROLES.includes(role.name);

  const { usePermissions, useUpdatePermissions, useRegistry } = useRoles();
  const { data: perms, isLoading, isError, refetch } = usePermissions(role.id);
  const { data: registry } = useRegistry();
  const updateMutation = useUpdatePermissions();

  const [search, setSearch] = useState('');
  const [onlySelected, setOnlySelected] = useState(false);
  // Unsaved changes only: permission value -> desired selection. Anything equal to the saved state is dropped.
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  // Explicit open/closed overrides per module; modules without one open when they have a ticked permission.
  const [openState, setOpenState] = useState<Record<string, boolean>>({});

  const claims = useMemo<RoleClaimResponse[]>(
    () => (perms?.roleClaims ?? []).map((c) => (c.value in edits ? { ...c, selected: edits[c.value] } : c)),
    [perms, edits]
  );
  const savedByValue = useMemo(() => new Map((perms?.roleClaims ?? []).map((c) => [c.value, c.selected])), [perms]);

  const editCount = Object.keys(edits).length;
  const added = Object.values(edits).filter(Boolean).length;
  const removed = editCount - added;
  const dirty = editCount > 0;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Leaving the page with unsaved edits would lose them silently.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const allGroups = useMemo(
    () => (registry ? buildRegistryGroups(claims, registry) : buildFallbackGroups(claims)),
    [claims, registry]
  );
  const groups = useMemo(() => filterGroups(allGroups, search, onlySelected), [allGroups, search, onlySelected]);

  const total = claims.length;
  const selectedTotal = claims.filter((c) => c.selected).length;
  const pct = total ? Math.round((selectedTotal / total) * 100) : 0;

  const setMany = (values: string[], checked: boolean) => {
    if (readOnly || values.length === 0) return;
    setEdits((prev) => {
      const next = { ...prev };
      for (const value of values) {
        if (savedByValue.get(value) === checked) delete next[value];
        else next[value] = checked;
      }
      return next;
    });
  };

  const isOpen = (group: PermissionGroup) =>
    openState[group.name] ?? (!!search.trim() || groupClaims(group).some((c) => c.selected));
  const setAllOpen = (open: boolean) =>
    setOpenState(Object.fromEntries(allGroups.map((g) => [g.name, open])));

  const handleSave = () => {
    if (readOnly || !dirty) return;
    updateMutation.mutate(
      {
        id: role.id,
        data: {
          roleId: role.id,
          roleClaims: claims.map((c) => ({ type: c.type, value: c.value, selected: c.selected })),
        },
      },
      { onSuccess: () => setEdits({}) }
    );
  };

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          Could not load permissions for {role.name}. Check your connection and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        '@media (prefers-reduced-motion: reduce)': { '& *': { transition: 'none !important' } },
      }}
    >
      {/* Role header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ShieldCheck size={18} color="#8A70D6" aria-hidden />
          <Typography component="h2" variant="h5" sx={{ fontWeight: 700 }}>
            {role.name}
          </Typography>
          <Chip
            size="small"
            icon={role.isGlobal ? <Lock size={11} /> : undefined}
            label={role.isGlobal ? 'System role' : 'Custom role'}
            sx={{
              height: 22,
              fontSize: '0.6875rem',
              bgcolor: (t) => alpha(t.palette.primary.main, role.isGlobal ? 0.16 : 0.08),
              color: 'primary.light',
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <Users size={13} aria-hidden />
            <Typography variant="body2">
              {role.userCount} {role.userCount === 1 ? 'user' : 'users'}
            </Typography>
          </Box>
        </Box>
        {role.description && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {role.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            aria-label="Share of permissions enabled"
            sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'action.hover' }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {selectedTotal} of {total} enabled
          </Typography>
        </Box>
      </Box>

      {/* Toolbar */}
      <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search modules, features or actions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: '1 1 220px', minWidth: 200 }}
          slotProps={{
            htmlInput: { 'aria-label': 'Search permissions' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={14} aria-hidden />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                    <X size={13} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <FormControlLabel
          sx={{ m: 0 }}
          control={<Switch size="small" checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)} />}
          label={<Typography variant="body2">Enabled only</Typography>}
        />
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" color="inherit" onClick={() => setAllOpen(true)}>Expand all</Button>
          <Button size="small" color="inherit" onClick={() => setAllOpen(false)}>Collapse all</Button>
          {!readOnly && (
            <>
              <Button
                size="small"
                color="inherit"
                onClick={() => setMany(claims.map((c) => c.value), true)}
                disabled={selectedTotal === total}
              >
                Select all
              </Button>
              <Button
                size="small"
                color="inherit"
                onClick={() => setMany(claims.map((c) => c.value), false)}
                disabled={selectedTotal === 0}
              >
                Clear all
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Permission list */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={48} />
            ))}
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {search ? `No permissions match “${search}”` : 'No enabled permissions'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {search ? 'Try a module name such as Attendance, or an action such as Export.' : 'Turn off “Enabled only” to see everything.'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSearch('');
                setOnlySelected(false);
              }}
            >
              Reset filters
            </Button>
          </Box>
        ) : (
          groups.map((group) => (
            <ModuleAccordion
              key={group.name}
              group={group}
              fullGroup={allGroups.find((g) => g.name === group.name) ?? group}
              open={isOpen(group)}
              onToggleOpen={(open) => setOpenState((prev) => ({ ...prev, [group.name]: open }))}
              readOnly={readOnly}
              onSet={setMany}
              savedByValue={savedByValue}
            />
          ))
        )}
      </Box>

      {/* Footer: always present so the layout does not jump when edits start */}
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          minHeight: 56,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: (t) => (dirty ? alpha(t.palette.primary.main, 0.08) : 'transparent'),
          transition: 'background-color 150ms ease',
        }}
      >
        {readOnly ? (
          <>
            <Lock size={14} aria-hidden />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This role always has full access and cannot be changed.
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="body2"
              role="status"
              aria-live="polite"
              sx={{ flex: 1, color: dirty ? 'text.primary' : 'text.secondary' }}
            >
              {dirty
                ? `${editCount} unsaved ${editCount === 1 ? 'change' : 'changes'} (${added} added, ${removed} removed)`
                : 'No unsaved changes'}
            </Typography>
            <Button
              size="small"
              color="inherit"
              startIcon={<RotateCcw size={13} />}
              onClick={() => setEdits({})}
              disabled={!dirty || updateMutation.isPending}
            >
              Discard
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Save size={13} />}
              onClick={handleSave}
              disabled={!dirty || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

interface ModuleAccordionProps {
  group: PermissionGroup;
  /** The module before search/filters, so the header counts always show the real totals. */
  fullGroup: PermissionGroup;
  open: boolean;
  onToggleOpen: (open: boolean) => void;
  readOnly: boolean;
  onSet: (values: string[], checked: boolean) => void;
  savedByValue: Map<string, boolean>;
}

function ModuleAccordion({ group, fullGroup, open, onToggleOpen, readOnly, onSet, savedByValue }: ModuleAccordionProps) {
  const moduleState = stateOf(groupClaims(fullGroup));
  const moduleTotal = groupClaims(fullGroup).length;
  const label = group.name.replace(/([A-Z])/g, ' $1').trim();

  return (
    <Accordion
      expanded={open}
      onChange={(_, expanded) => onToggleOpen(expanded)}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        bgcolor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'none',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px !important',
        mb: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={16} aria-hidden />}
        aria-label={`${label}, ${moduleState.selected} of ${moduleTotal} enabled`}
        sx={{
          px: 1.5,
          minHeight: 48,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
        }}
      >
        <Checkbox
          size="small"
          checked={moduleState.all}
          indeterminate={moduleState.some}
          disabled={readOnly}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
          onChange={(e) => onSet(groupClaims(fullGroup).map((c) => c.value), e.target.checked)}
          slotProps={{ input: { 'aria-label': `Enable all ${label} permissions` } }}
          sx={{ p: 0.5 }}
        />
        <Typography variant="body1" sx={{ fontWeight: 700, flex: 1 }}>
          {label}
        </Typography>
        <Chip
          size="small"
          label={`${moduleState.selected}/${moduleTotal}`}
          sx={{
            height: 22,
            fontSize: '0.6875rem',
            fontVariantNumeric: 'tabular-nums',
            bgcolor: (t) => (moduleState.selected ? alpha(t.palette.primary.main, 0.16) : 'action.hover'),
            color: moduleState.selected ? 'primary.light' : 'text.secondary',
          }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 0.5 }}>
        {group.sections.map((section) => (
          <FeatureRow
            key={section.key}
            section={section}
            readOnly={readOnly}
            onSet={onSet}
            savedByValue={savedByValue}
            showTitle={group.sections.length > 1 || !!section.title}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

interface FeatureRowProps {
  section: PermissionSection;
  readOnly: boolean;
  onSet: (values: string[], checked: boolean) => void;
  savedByValue: Map<string, boolean>;
  showTitle: boolean;
}

function FeatureRow({ section, readOnly, onSet, savedByValue, showTitle }: FeatureRowProps) {
  const claims = section.items.map((i) => i.claim);
  const state = stateOf(claims);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: { xs: 0.5, md: 2 },
        px: 1.5,
        py: 0.75,
        borderTop: 1,
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background-color 150ms ease',
      }}
    >
      {showTitle && (
        <Box sx={{ width: { md: 240 }, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Checkbox
            size="small"
            checked={state.all}
            indeterminate={state.some}
            disabled={readOnly}
            onChange={(e) => onSet(claims.map((c) => c.value), e.target.checked)}
            slotProps={{ input: { 'aria-label': `Enable all ${section.title ?? 'feature'} actions` } }}
            sx={{ p: 0.5 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {section.title}
            </Typography>
            {section.description && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {section.description}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, pl: { xs: showTitle ? 4 : 0, md: 0 } }}>
        {section.items.map(({ label, claim }) => {
          const changed = claim.selected !== (savedByValue.get(claim.value) ?? claim.selected);
          return (
            <FormControlLabel
              key={claim.value}
              sx={{
                m: 0,
                pr: 1.25,
                borderRadius: 1,
                border: 1,
                borderColor: changed ? 'primary.main' : 'transparent',
                bgcolor: (t) => (claim.selected ? alpha(t.palette.primary.main, 0.1) : 'transparent'),
                transition: 'background-color 150ms ease, border-color 150ms ease',
                '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                '& .MuiFormControlLabel-label': { fontSize: '0.75rem', fontWeight: 500 },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={claim.selected}
                  disabled={readOnly}
                  onChange={(e) => onSet([claim.value], e.target.checked)}
                  sx={{ p: 0.75 }}
                />
              }
              label={label}
            />
          );
        })}
      </Box>
    </Box>
  );
}
